import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Grade3Pinyin.css";

/** Shuffle array for random quiz questions */
function shuffleArray(arr) {
  const cloned = [...arr];
  for (let i = cloned.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
}

/**
 * Grade3ChineseLesson
 *  - Two Topics: "Pinyin (Daily Conversation)" & "Zhuyin (Daily Conversation)"
 *  - Each has 25 Q => total 50 final quiz
 *  - Focus on more advanced words & daily conversation practice
 */
export default function Grade3ChineseLesson() {
  // track which topic is selected
  const [selectedTopic, setSelectedTopic] = useState(null);

  // (1) LESSON CONTENT
  const lessonContent = {
    pinyin: {
      title: "Pinyin: Daily Conversation & New Words",
      description: "Learn more advanced daily phrases, questions, and new vocabulary via Pinyin!",
      example: "Example: wǒ qù shāngdiàn mǎi dōngxi (我去商店买东西) = 'I go to the store to buy stuff.'",
      steps: [
        "Practice forming questions: nǐ zěnme le? (你怎么了？) 'What’s wrong?' or 'Are you okay?'",
        "Expand daily usage: jīntiān xīngqī jǐ? (今天星期几？) 'What day is it today?'",
        "Use connecting words: yīnwèi (because), suǒyǐ (therefore), kěshì (but).",
        "New phrases: qǐngwèn zhèlǐ néng huàn qián ma? (请问这里能换钱吗？) 'Excuse me, can I exchange money here?'",
        "Vocabulary: jiārén (家人, 'family members'), péngyǒu (朋友, 'friends'), gōngkè (功课, 'homework').",
      ],
    },
    zhuyin: {
      title: "Zhuyin: Daily Conversation & New Words",
      description: "Use Zhuyin (bopomofo) for everyday dialogues, questions, and expanded vocabulary!",
      example: "Example: ㄨㄛˇ ㄑㄩˋ ㄕㄤ ㄉㄧㄢˋ ㄇㄞˇ ㄉㄨㄥ ㄒㄧ (我去商店买东西).",
      steps: [
        "Ask polite questions: ㄋㄧˇ ㄗㄣˇ ㄇㄜ˙ ㄌㄜ˙? (你怎么了？) 'What happened?' or 'Are you okay?'",
        "Days of the week: ㄒㄧㄥ ㄑㄧˊ ㄐㄧˇ (星期几), 'Which day is it?'",
        "Connecting words: ㄧㄣ ㄨㄟˋ (因为), ㄙㄨㄛˇ ㄧˇ (所以), ㄎㄜˇ ㄕˋ (可是).",
        "More sentences: ㄑㄧㄥˇ ㄨㄣˋ ㄓㄜˋ ㄌㄧˇ ㄋㄥˊ ㄏㄨㄢˋ ㄑㄧㄢˊ ㄇㄚ˙? (请问这里能换钱吗？)",
        "Vocabulary: ㄐㄧㄚ ㄖㄣˊ (家人), ㄆㄥˊ ㄧㄡˇ (朋友), ㄍㄨㄥ ㄎㄜˋ (功课).",
      ],
    },
  };

  // (2) QUIZ POOLS => each 25 => total 50
  // Pinyin => 25 Q
  const pinyinPool = [
    {
      question: "Which phrase means 'What day is it today?' in Pinyin?",
      correctAnswer: "jīntiān xīngqī jǐ",
      options: [
        "nǐ zěnme le",
        "jīntiān xīngqī jǐ",
        "zhèlǐ néng huàn qián ma",
      ],
    },
    {
      question: "The word 'yīnwèi' (因为) means?",
      correctAnswer: "Because",
      options: [
        "But",
        "Therefore",
        "Because",
      ],
    },
    {
      question: "Which Pinyin for 'What’s wrong?' (你怎么了？)",
      correctAnswer: "nǐ zěnme le",
      options: [
        "nǐ hǎo le",
        "nǐ zěnme le",
        "zěnme méiyǒu nǐ",
      ],
    },
    {
      question: "If you want to say 'I go to the store to buy stuff' in Pinyin, you'd say?",
      correctAnswer: "wǒ qù shāngdiàn mǎi dōngxi",
      options: [
        "nǐ bù qù shāngdiàn",
        "wǒ qù shāngdiàn mǎi dōngxi",
        "nǐ qù shāngdiàn hē shuǐ",
      ],
    },
    {
      question: "How do you pronounce 家人 (family members) in Pinyin?",
      correctAnswer: "jiārén (1st + 2nd tone)",
      options: [
        "jiāren (1st + neutral)",
        "jiārén (1st + 2nd)",
        "jiǎrén (3rd + 2nd)",
      ],
    },
    {
      question: "Which phrase might mean 'Can I exchange money here?'",
      correctAnswer: "zhèlǐ néng huàn qián ma?",
      options: [
        "zhège duōshǎo qián?",
        "zhèlǐ néng huàn qián ma?",
        "nǐ huàn bù huàn?",
      ],
    },
    {
      question: "The word 'gōngkè' (功课) means?",
      correctAnswer: "Homework or schoolwork",
      options: [
        "Crafts or handmade items",
        "Homework or schoolwork",
        "Restaurant cooking",
      ],
    },
    {
      question: "If someone says 'Wǒ méiyǒu péngyǒu,' it means?",
      correctAnswer: "I don’t have friends",
      options: [
        "I don’t want to eat",
        "I don’t have friends",
        "I have many pencils",
      ],
    },
    {
      question: "Which connecting word means 'therefore' in Pinyin?",
      correctAnswer: "suǒyǐ",
      options: [
        "kěshì",
        "yīnwèi",
        "suǒyǐ",
      ],
    },
    {
      question: "The phrase 'nǐ zěnme le?' could also mean?",
      correctAnswer: "'What's up?' or 'What's the matter?'",
      options: [
        "'Where are you?'",
        "'What’s up?' or 'What's the matter?'",
        "'I need help right now'",
      ],
    },
    {
      question: "To ask 'Do you want coffee or tea?' => 'nǐ yào kāfēi háishì chá?' The word 'háishì' means?",
      correctAnswer: "Or (in a question)",
      options: [
        "But",
        "Therefore",
        "Or (in a question)",
      ],
    },
    {
      question: "If you say 'kěshì wǒ méiyǒu qián,' you mean?",
      correctAnswer: "But I don’t have money",
      options: [
        "And I don’t want money",
        "Because I have money",
        "But I don’t have money",
      ],
    },
    {
      question: "Which might be a short phrase: 'wǒ xiǎng qǐng nǐ chī wǎnfàn' means?",
      correctAnswer: "I’d like to treat you to dinner",
      options: [
        "I want you to cook dinner",
        "I have no dinner for you",
        "I’d like to treat you to dinner",
      ],
    },
    {
      question: "If you see 'jīntiān hěn lěng, suǒyǐ wǒ chuān dàyī,' it means?",
      correctAnswer: "It’s cold today, so I wear a coat",
      options: [
        "It’s hot today, so I take off my coat",
        "It’s cold, so I wear a coat",
        "It’s raining, so no coat needed",
      ],
    },
    {
      question: "To politely ask 'Where do you live?' => 'nǐ zhù zài nǎlǐ?' The 'zhù' means?",
      correctAnswer: "To live/reside",
      options: [
        "To eat",
        "To live/reside",
        "To walk",
      ],
    },
    {
      question: "'yīnwèi wǒ gōngkè duō, suǒyǐ wǒ bùnéng chūqù' means?",
      correctAnswer: "Because I have lots of homework, therefore I can’t go out",
      options: [
        "I want to go out for homework",
        "I can go out, but I don’t want to",
        "Because I have lots of homework, I can’t go out",
      ],
    },
    {
      question: "How to say 'friends' in Pinyin?",
      correctAnswer: "péngyǒu",
      options: [
        "péngyóu",
        "pèngyóu",
        "péngyǒu",
      ],
    },
    {
      question: "If you want to say 'Excuse me, can I ask...?' => 'qǐngwèn, wǒ kěyǐ wèn...?' The phrase 'qǐngwèn' means?",
      correctAnswer: "'May I ask...?' or 'Excuse me...'",
      options: [
        "I’m sorry",
        "Thank you a lot",
        "'May I ask...?' or 'Excuse me...'",
      ],
    },
    {
      question: "The question 'jīntiān nǐ yǒu kòng ma?' means?",
      correctAnswer: "'Are you free today?'",
      options: [
        "'Do you have money today?'",
        "'Are you free today?'",
        "'Where are you going?'",
      ],
    },
    {
      question: "Which phrase means 'I want to buy this book' in Pinyin?",
      correctAnswer: "wǒ yào mǎi zhè běn shū",
      options: [
        "wǒ yào mǎi zhè běn shū",
        "nǐ yào mài zhè bēi shuǐ",
        "zhè běn shū bú mǎi",
      ],
    },
    {
      question: "‘jiārén dōu hǎo ma?’ means?",
      correctAnswer: "'Is your family all well?'",
      options: [
        "'Where is your home?'",
        "'Is your family all well?'",
        "'Do you have money?'",
      ],
    },
    {
      question: "'nǐ yǒu méiyǒu yùdìng wǒmen de huǒchē piào?' means?",
      correctAnswer: "Have you booked our train tickets?",
      options: [
        "Where are we going by train?",
        "How many tickets do we have?",
        "Have you booked our train tickets?",
      ],
    },
    {
      question: "The phrase 'Zhè shì wǒde péngyǒu' means?",
      correctAnswer: "'This is my friend'",
      options: [
        "'This is my father'",
        "'This is my friend'",
        "'These are my books'",
      ],
    },
    {
      question: "When you see 'kàn qǐlái hěn hǎo,' it means?",
      correctAnswer: "It looks very good/nice",
      options: [
        "I don’t like it",
        "It looks very good",
        "I’m feeling bad",
      ],
    },
  ];

  // Zhuyin => 25 Q
  const zhuyinPool = [
    {
      question: "Which Zhuyin might express 'nǐ zhù zài nǎlǐ?' (Where do you live?)",
      correctAnswer: "ㄋㄧˇ ㄓㄨˋ ㄗㄞˋ ㄋㄚˇ ㄌㄧˇ",
      options: [
        "ㄋㄧˇ ㄗㄨㄛˋ ㄓㄞˋ ㄋㄞˇ ㄌㄧˇ",
        "ㄋㄧˇ ㄓㄨˋ ㄗㄞˋ ㄋㄚˇ ㄌㄧˇ",
        "ㄋㄧˊ ㄔㄨㄞˋ ㄋㄚˇ ㄌㄧˇ",
      ],
    },
    {
      question: "‘qǐngwèn, ㄨㄛˇ ㄎㄜˇ ㄧˇ ㄨㄣˋ...?’ stands for?",
      correctAnswer: "qǐngwèn, wǒ kěyǐ wèn...? (Excuse me, may I ask...?)",
      options: [
        "wǒ bù yào wèn",
        "nǐ qǐng kàn",
        "qǐngwèn, wǒ kěyǐ wèn...?",
      ],
    },
    {
      question: "If you see ㄐㄧㄢˋ ㄊㄧㄢ ㄋㄧˇ, you might interpret it as 'jiàn tiān nǐ'—makes sense?",
      correctAnswer: "Could mean 'See you today'? Not a typical phrase. Maybe missing context.",
      options: [
        "Yes, it’s definitely 'good morning'",
        "No sense at all",
        "Might mean 'see you today,' but incomplete context",
      ],
    },
    {
      question: "Asking '今天星期几？' in Zhuyin might be?",
      correctAnswer: "ㄐㄧㄣ ㄊㄧㄢ ㄒㄧㄥ ㄑㄧˊ ㄐㄧˇ",
      options: [
        "ㄐㄧㄣ ㄖˋ ㄒㄧㄥ ㄐㄧˇ",
        "ㄐㄧㄣ ㄊㄧㄢ ㄒㄧㄥ ㄑㄧˊ ㄐㄧˇ",
        "ㄐㄧ ㄊㄧㄢ ㄎㄨㄥ ㄑㄧˋ",
      ],
    },
    {
      question: "‘你怎么了？’ => ㄋㄧˇ ㄗㄣˇ ㄇㄜ˙ ㄌㄜ˙, meaning?",
      correctAnswer: "What's wrong?",
      options: [
        "Where are you?",
        "What's wrong?",
        "Are you hungry?",
      ],
    },
    {
      question: "To express '我去商店买东西' in Zhuyin: ㄨㄛˇ ㄑㄩˋ ㄕㄤ ㄉㄧㄢˋ ㄇㄞˇ ㄉㄨㄥ ㄒㄧ means?",
      correctAnswer: "'I go to the store to buy stuff'",
      options: [
        "I’m leaving the store with nothing",
        "'I go to the store to buy stuff'",
        "I don’t want to buy anything",
      ],
    },
    {
      question: "The word '朋友' => ㄆㄥˊ ㄧㄡˇ (péngyǒu). Means?",
      correctAnswer: "Friends",
      options: [
        "Family",
        "Teacher",
        "Friends",
      ],
    },
    {
      question: "ㄧㄣ ㄨㄟˋ (因为) vs. ㄙㄨㄛˇ ㄧˇ (所以) means?",
      correctAnswer: "Because vs. therefore",
      options: [
        "Hello vs. sorry",
        "Because vs. therefore",
        "But vs. also",
      ],
    },
    {
      question: "‘家人’ => ㄐㄧㄚ ㄖㄣˊ, might read as 'jiā rén.' Means?",
      correctAnswer: "'Family members'",
      options: [
        "'Neighbors'",
        "'Family members'",
        "'School mates'",
      ],
    },
    {
      question: "If you see ㄋㄧˇ ㄧㄡˇ ㄇㄟˊ ㄧㄡˇ ㄆㄥˊ ㄧㄡˇ, it’s asking?",
      correctAnswer: "'Do you have friends?'",
      options: [
        "'Do you want to eat?'",
        "'Do you have friends?'",
        "'How old are you?'",
      ],
    },
    {
      question: "‘kěshì’ => ㄎㄜˇ ㄕˋ means?",
      correctAnswer: "'But' or 'however'",
      options: [
        "'Because'",
        "'But'",
        "'Therefore'",
      ],
    },
    {
      question: "Asking politely: ㄑㄧㄥˇ ㄨㄣˋ, ㄓㄜˋ ㄌㄧˇ ㄋㄥˊ ㄏㄨㄢˋ ㄑㄧㄢˊ ㄇㄚ˙?",
      correctAnswer: "'Excuse me, can I exchange money here?'",
      options: [
        "'Do you have water?'",
        "'Excuse me, can I exchange money here?'",
        "'Where is my friend?'",
      ],
    },
    {
      question: "日常对话: ㄨㄛˇ ㄒㄧㄤˇ ㄑㄧㄥˇ ㄋㄧˇ ㄔ ㄨㄢˇ ㄈㄢˋ => 'wǒ xiǎng qǐng nǐ chī wǎnfàn' means?",
      correctAnswer: "'I want to treat you to dinner'",
      options: [
        "'I want to watch you cook dinner'",
        "'I want to treat you to dinner'",
        "'Let’s skip dinner tonight'",
      ],
    },
    {
      question: "‘功课’ => ㄍㄨㄥ ㄎㄜˋ, means 'homework/schoolwork.' So if you say ㄨㄛˇ ㄧㄡˇ ㄏㄣˇ ㄉㄨㄛ ㄍㄨㄥ ㄎㄜˋ, implies?",
      correctAnswer: "I have a lot of homework",
      options: [
        "I have no homework",
        "I have a lot of money",
        "I have a lot of homework",
      ],
    },
    {
      question: "‘我很忙，所以没时间出去’ => ㄨㄛˇ ㄏㄣˇ ㄇㄤˊ, ㄙㄨㄛˇ ㄧˇ ㄇㄟˊ ㄕˊ ㄐㄧㄢ ㄔㄨ ㄑㄩ => means?",
      correctAnswer: "I'm very busy, so I have no time to go out",
      options: [
        "I’m free, so let’s go out",
        "I’m very busy, so no time to go out",
        "I have no idea about going out",
      ],
    },
    {
      question: "ㄑㄧㄥˇ ㄨㄣˋ: ㄐㄧㄣ ㄊㄧㄢ ㄋㄧˇ ㄧㄡˇ ㄎㄨㄥˋ ㄇㄚ˙ => 'jīntiān nǐ yǒu kòng ma?' means?",
      correctAnswer: "'Are you free today?'",
      options: [
        "'Are you hungry today?'",
        "'Are you free today?'",
        "'Do you have money today?'",
      ],
    },
    {
      question: "To say 'It looks nice' => ㄎㄢˋ ㄑㄧˇ ㄌㄞˊ ㄏㄣˇ ㄏㄠˇ => 'kàn qǐlái hěn hǎo' means?",
      correctAnswer: "It looks really good",
      options: [
        "It’s bad or broken",
        "It’s not what we need",
        "It looks really good",
      ],
    },
    {
      question: "If you see ㄋㄧˇ ㄏㄨㄞˋ ㄌㄜ˙? => 'nǐ huài le?' might be a joking phrase meaning?",
      correctAnswer: "'Are you being bad?' or 'You broke it?' context-based",
      options: [
        "You are nice",
        "You are hungry",
        "'Are you being bad?' / 'You broke it?'",
      ],
    },
    {
      question: "‘我想跟你去商店’ => ㄨㄛˇ ㄒㄧㄤˇ ㄍㄣ ㄋㄧˇ ㄑㄩˋ ㄕㄤ ㄉㄧㄢˋ means?",
      correctAnswer: "I want to go to the store with you",
      options: [
        "I want to go home now",
        "I want you to go alone",
        "I want to go to the store with you",
      ],
    },
    {
      question: "Asking 'Can you help me?' => ㄋㄧˇ ㄎㄜˇ ㄧˇ ㄅㄤ ㄨㄛˇ ㄇㄚ˙ => 'nǐ kěyǐ bāng wǒ ma?' means?",
      correctAnswer: "Can you help me?",
      options: [
        "Are you looking at me?",
        "Where is your home?",
        "Can you help me?",
      ],
    },
    {
      question: "‘dōngxi’ => ㄉㄨㄥ ㄒㄧ, means 'things/stuff.' So if you see ㄨㄛˇ ㄇㄞˇ ㄉㄨㄥ ㄒㄧ => 'wǒ mǎi dōngxi' means?",
      correctAnswer: "I'm buying things",
      options: [
        "I’m selling everything",
        "I’m watching something",
        "I’m buying things",
      ],
    },
    {
      question: "‘因为我累了，所以想休息’ => ㄧㄣ ㄨㄟˋ ㄨㄛˇ ㄌㄟˋ ㄌㄜ˙, ㄙㄨㄛˇ ㄧˇ ㄒㄧㄤˇ ㄒㄧㄡ ㄒㄧ => means?",
      correctAnswer: "Because I’m tired, therefore I want to rest",
      options: [
        "Because I’m hungry, I want to rest",
        "Because I’m tired, I want to rest",
        "I want to work more",
      ],
    },
    {
      question: "If you see ㄨㄛˇ ㄒㄧ ㄏㄨㄢˇ ㄊㄧㄠˋ ㄨˇ => 'wǒ xǐ huān tiào wǔ,' means?",
      correctAnswer: "I like to dance",
      options: [
        "I like to run",
        "I like to dance",
        "I’m going to sleep",
      ],
    },
    {
      question: "Which best describes advanced daily conversation in Zhuyin?",
      correctAnswer: "Combining multiple characters & tone marks to form fluid sentences",
      options: [
        "Always single-character isolation",
        "No tone marks needed",
        "Multiple characters & tone marks for fluid sentences",
      ],
    },
  ];

  // (3) QUICK QUIZ & FINAL QUIZ states
  const [quiz, setQuiz] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [finalQuizQuestions, setFinalQuizQuestions] = useState([]);
  const [finalQuizActive, setFinalQuizActive] = useState(false);
  const [finalQuizIndex, setFinalQuizIndex] = useState(0);
  const [finalQuizScore, setFinalQuizScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(300); // 5 mins

  // (4) Timer effect
  useEffect(() => {
    if (finalQuizActive && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFinalQuizActive(false);
    }
  }, [finalQuizActive, timeLeft]);

  // (5) Generate Quick Quiz
  const generateQuiz = (topic) => {
    let questionItem;
    if (topic === "pinyin") {
      questionItem = pinyinPool[Math.floor(Math.random() * pinyinPool.length)];
    } else {
      // zhuyin
      questionItem = zhuyinPool[Math.floor(Math.random() * zhuyinPool.length)];
    }
    setQuiz({
      question: questionItem.question,
      correctAnswer: questionItem.correctAnswer,
      options: shuffleArray([...questionItem.options]),
    });
    setSelectedAnswer(null);
    setFeedback(null);
  };

  // (6) Quick quiz answer
  const handleAnswerClick = (answer) => {
    setSelectedAnswer(answer);
    if (quiz && answer === quiz.correctAnswer) {
      setFeedback("✅ Correct!");
    } else {
      setFeedback("❌ Try Again!");
    }
    // Refresh question after short delay
    setTimeout(() => {
      if (selectedTopic) generateQuiz(selectedTopic);
    }, 1500);
  };

  // (7) Start Final Quiz => combine 2 pools => 50
  const startFinalQuiz = () => {
    const combined = [...pinyinPool, ...zhuyinPool];
    const finalSet = shuffleArray(combined);
    setFinalQuizQuestions(finalSet);
    setFinalQuizActive(true);
    setFinalQuizIndex(0);
    setFinalQuizScore(0);
    setTimeLeft(300);
  };

  // (8) Final quiz answer
  const handleFinalQuizAnswer = (choice) => {
    if (finalQuizQuestions[finalQuizIndex] && choice === finalQuizQuestions[finalQuizIndex].correctAnswer) {
      setFinalQuizScore(prev => prev + 1);
    }
    if (finalQuizIndex < finalQuizQuestions.length - 1) {
      setFinalQuizIndex(prev => prev + 1);
    } else {
      // finished
      setFinalQuizActive(false);
    }
  };

  return (
    <div className="g3chi-page">
      <h1 className="g3chi-title">Grade 3 Chinese Lessons</h1>
      <p className="g3chi-description">Pick a system to practice: Pinyin or Zhuyin!</p>

      <div className="g3chi-topics">
        <button
          className="g3chi-topic-card"
          onClick={() => {
            setSelectedTopic("pinyin");
            generateQuiz("pinyin");
          }}
        >
          Pinyin (Daily Conversation)
        </button>
        <button
          className="g3chi-topic-card"
          onClick={() => {
            setSelectedTopic("zhuyin");
            generateQuiz("zhuyin");
          }}
        >
          Zhuyin (Daily Conversation)
        </button>
      </div>

      {selectedTopic && (
        <div className="g3chi-lesson-quiz-container">
          {/* LESSON CONTENT */}
          <div className="g3chi-lesson-content">
            <h2 className="g3chi-lesson-title">{lessonContent[selectedTopic].title}</h2>
            <p className="g3chi-lesson-description">
              {lessonContent[selectedTopic].description}
            </p>
            <p className="g3chi-lesson-example">
              <strong>{lessonContent[selectedTopic].example}</strong>
            </p>
            <ul className="g3chi-lesson-steps">
              {lessonContent[selectedTopic].steps.map((step, idx) => (
                <li key={idx}>{step}</li>
              ))}
            </ul>
          </div>

          {/* QUIZZES (Right) */}
          <div className="g3chi-quiz-double-container">
            {/* Quick Quiz */}
            <div className="g3chi-quick-quiz-container">
              <h2 className="g3chi-quiz-title">Quick Quiz!</h2>
              {quiz && (
                <>
                  <p className="g3chi-quiz-question">{quiz.question}</p>
                  <div className="g3chi-quiz-options">
                    {quiz.options.map((opt, i) => (
                      <button
                        key={i}
                        className={`g3chi-quiz-btn ${selectedAnswer === opt ? "selected" : ""}`}
                        onClick={() => handleAnswerClick(opt)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  {feedback && <p className="g3chi-quiz-feedback">{feedback}</p>}
                </>
              )}
            </div>

            {/* Final Quiz */}
            <div className="g3chi-final-quiz-container">
              <h2 className="g3chi-quiz-title">Final Quiz</h2>
              {!finalQuizActive && (
                <button className="g3chi-final-quiz-button" onClick={startFinalQuiz}>
                  Start Final Quiz (50 Questions)
                </button>
              )}
              {finalQuizActive && (
                <div className="g3chi-final-quiz-content">
                  <p>
                    Time Left: {Math.floor(timeLeft / 60)}:
                    {String(timeLeft % 60).padStart(2, "0")}
                  </p>
                  <p>Question {finalQuizIndex + 1} of {finalQuizQuestions.length}</p>
                  <p className="g3chi-quiz-question">
                    {finalQuizQuestions[finalQuizIndex].question}
                  </p>
                  <div className="g3chi-quiz-options">
                    {finalQuizQuestions[finalQuizIndex].options.map((choice, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleFinalQuizAnswer(choice)}
                      >
                        {choice}
                      </button>
                    ))}
                  </div>
                  <p>Score: {finalQuizScore}</p>
                  {finalQuizIndex === finalQuizQuestions.length - 1 && (
                    <p>Final Score: {finalQuizScore}/{finalQuizQuestions.length}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Link to="/chineseSubject" className="g3chi-back-button">
        ← Back to Chinese Page
      </Link>
    </div>
  );
}
